import {withAuthenticationRequired} from "@auth0/auth0-react";
import Loading from "../../components/common/Loading";
import React, {useContext, useEffect, useState} from "react";
import {
    Alert,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    FormControlLabel,
    Paper, Radio, RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {useReservationAPI} from "../../service/useReservationAPI";
import {GlobalStateContext} from "../../state/GlobalStateProvider";
import AppSnackbar from "../../common/AppSnackbar";
import {useTranslation} from "react-i18next";
import {useTenantConfig} from "../../security/tenant-config-context";

function SelectStay({availableStays, updateStays, updateReservationStep, isLoadingInParent}) {
    const {t} = useTranslation();
    const {getStaysFromReservation} = useReservationAPI();
    const {globalState, setPartOfGlobalState} = useContext(GlobalStateContext);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const { cfg } = useTenantConfig();
    const isDogBusiness = cfg?.animalTypes?.includes('DOG');

    useEffect(() => {
        setIsLoading(true)
        getStaysFromReservation(globalState.reservationUuid)
            .then(value => {
                if (value) {
                    updateStays(value);
                }
            })
            .catch(() => handleFetchError(t('fetchStayInReservationFailed')))
            .finally(() => {
                setIsLoading(false)
            });
    }, []);

    function handleFetchError(reason) {
        setSnackbarMessage(reason)
        setOpenSnackbar(true)
    }

    const getLabel = (stayCombo) => {
        return t('selectStayDisplay', {
            price: stayCombo.totalPricePerDay,
            count: stayCombo.totalPets,
            animalTypeLabel: isDogBusiness ?  t('animalTypeLabel.DOG') : t('animalTypeLabel.CAT'),
        });
    }

    const getLineLabel = (line) => {
        return t('selectStayLineDisplay', {
            count: line.petsAssigned,
            animalTypeLabel: isDogBusiness ?  t('animalTypeLabel.DOG') : t('animalTypeLabel.CAT'),
            name: line.stayName
        });
    }

    return (
        <>
            {isLoading && isLoadingInParent && (<Loading/>)}
            {!isLoading && !isLoadingInParent && (
                <Card>
                    <CardHeader title={<Typography color="secondary" variant="h5">{t('selectStayTitle')}</Typography>}/>
                    <CardContent>
                        {availableStays.length > 0 && (<TableContainer component={Paper} sx={{marginTop: 5}}>
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>{t('selectStay')}</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <RadioGroup
                                        aria-labelledby="demo-radio-buttons-group-label"
                                        name="radio-buttons-group"
                                    >
                                        {
                                            availableStays.map((stayCombo, index) => (
                                                <TableRow key={index} sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                                    <TableCell component="th" scope="row">
                                                        <FormControlLabel
                                                            checked={globalState.reservationMeta.selectedCombo === stayCombo}
                                                            value={index}
                                                            control={<Radio inputProps={{ "data-testid": `stay-combo-radio-${index}` }}/>}
                                                            label={
                                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                                        {getLabel(stayCombo)}
                                                                    </Typography>
                                                                    {stayCombo.lines.map((line, lineIndex) => (
                                                                        <Typography key={lineIndex} variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                                                                            • {getLineLabel(line)}
                                                                        </Typography>
                                                                    ))}
                                                                </div>
                                                            }
                                                            onChange={() => {
                                                                setPartOfGlobalState('reservationMeta.selectedCombo', stayCombo)
                                                            }}
                                                            data-testid={`stay-combo-label-${index}`}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        }
                                    </RadioGroup>
                                </TableBody>
                            </Table>
                        </TableContainer>)}

                        {!isLoading && !isLoadingInParent && availableStays.length === 0 && (
                            <Alert sx={{marginTop: 2}} severity="warning" data-testid="no-available-stays-alert">{t('noAvailableStays')}</Alert>
                        )}
                    </CardContent>
                    <CardActions style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        {availableStays.length > 0 && (
                            <>
                                <Button
                                    onClick={() => {
                                        setPartOfGlobalState('reservationMeta.selectedCombo', undefined)
                                        updateReservationStep(2);
                                    }}
                                    variant="contained"
                                    data-testid="previous-button">
                                    {t('previous')}
                                </Button>
                                <Button disabled={globalState.reservationMeta.selectedCombo === undefined} onClick={() => updateReservationStep(4)} variant="contained" data-testid="next-button">
                                    {t('next')}
                                </Button></>
                        )}

                        {availableStays.length === 0 && (<Button onClick={() => updateReservationStep(1)} variant="contained" data-testid="update-dates-button">
                            {t('updateDates')}
                        </Button>)}
                    </CardActions>
                    <AppSnackbar openSnackbar={openSnackbar} snackbarMessage={snackbarMessage} setOpenSnackbar={setOpenSnackbar}/>
                </Card>)}
        </>
    )
}

export default withAuthenticationRequired(SelectStay, {
    onRedirecting: () => <Loading/>,
    returnTo: '/nieuwe-reservatie'
});
